import { createContext, useContext, ReactNode } from "react";
import { authClient } from "../lib/auth-client";

export const FREE_TIER_TICKER = "NVDA";

interface MembershipContextType {
  isMembershipActive: boolean;
  isLoading: boolean;
}

const MembershipContext = createContext<MembershipContextType | undefined>(
  undefined
);

interface MembershipProviderProps {
  children: ReactNode;
}

/**
 * Session-derived membership flag, safe on ANY route: it reads the auth
 * session directly, so no provider is required. Hooks that render on
 * provider-less routes (e.g. useChatQuota inside account settings) use this;
 * the provider below just shares the same value through context.
 */
export const useIsMembershipActive = (): MembershipContextType => {
  const { data, isPending } = authClient.useSession();

  // During session revalidation (isPending), preserve the last known membership
  // state to prevent false-negative flashes that trigger erroneous redirects.
  // Only resolve to false when we have a definitive non-pending response.
  const isMembershipActive = isPending
    ? (data?.session?.is_membership_active ?? true)
    : (data?.session?.is_membership_active ?? false);

  return { isMembershipActive, isLoading: isPending };
};

export const MembershipProvider = ({ children }: MembershipProviderProps) => {
  const value = useIsMembershipActive();

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
};

export const useMembership = (): MembershipContextType => {
  const context = useContext(MembershipContext);
  if (context === undefined) {
    throw new Error("useMembership must be used within a MembershipProvider");
  }
  return context;
};
